import { Timeline, timelineClasses, timelineItemClasses } from '@mui/lab'
import { Box, Theme } from '@mui/material'
import { Children } from 'react'
import useStyles from '~/common/hooks/useStyles'
import { PaginatedComment, PaginatedEvent } from '../types'
import { ActivityItem } from './ActivityItem'

export type DossActivityTimelineStyles = ReturnType<typeof getDossActivityTimelineStyles>

export function getDossActivityTimelineStyles(theme: Theme) {
  return {
    timeline: {
      [`& .${timelineItemClasses.root}:before`]: {
        flex: 0,
        padding: 0,
      },
      [`& .${timelineClasses.positionRight}`]: {
        flex: 0,
        flexGrow: 0,
      },
      margin: 0,
    },
    timelineItem: {
      paddingBottom: theme.spacing(4),
    },
    item: {
      maxWidth: '100%',
      alignItems: 'center',
    },
    separator: {
      alignSelf: 'normal',
      marginTop: theme.spacing(1),
    },
    icon: {
      fontSize: '12px',
      padding: '6px',
      marginTop: '1px',
      display: 'flex',
    },
    content: {
      paddingX: theme.spacing(1),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
    details: {
      whiteSpace: 'pre-wrap',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
    breadcrumbs: {
      display: 'flex',
      alignItems: 'center',
      paddingBottom: theme.spacing(1),
      '& .MuiBreadcrumbs-separator': {
        margin: 0,
      },
      '& .MuiButton-root': {
        paddingBlock: theme.spacing(0),
        paddingInline: theme.spacing(0.5),
        minWidth: 0,
      },
    },
    events: {
      statusChip: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flexShrink: 1,
        maxWidth: 'calc(100% - 10px)',
        backgroundColor: theme.palette.common.doss.gray[1],
        color: theme.palette.common.doss.gray[10],
        borderRadius: '4px',
        failure: {
          backgroundColor: theme.palette.common.doss.red[2],
          color: theme.palette.common.doss.red[10],
          border: `1px solid ${theme.palette.common.doss.red[10]}`,
        },
        success: {
          backgroundColor: theme.palette.common.doss.green[2],
          color: theme.palette.common.doss.green[10],
          border: `1px solid ${theme.palette.common.doss.green[10]}`,
        },
        errorMessage: {
          paddingTop: theme.spacing(1),
        },
      },
      updateTableCell: {
        stack: {
          flexShrink: 0,
          overflow: 'hidden',
          maxWidth: '100%',
          display: 'flex',
        },
        chips: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexShrink: 1,
          maxWidth: 'calc(50% - 20px)', // defined because we have 2 chips and an arrow icon
          backgroundColor: theme.palette.common.doss.gray[1],
          color: theme.palette.common.doss.gray[9],
          borderRadius: '4px',
        },
      },
      updateChart: {
        stack: {
          flexShrink: 0,
          overflow: 'hidden',
          maxWidth: '100%',
          display: 'flex',
        },
        fullLineChips: {
          display: 'flex',
          gap: 1,
          flexShrink: 1,
          backgroundColor: theme.palette.common.doss.gray[1],
          color: theme.palette.common.doss.gray[9],
          borderRadius: '2px',
          marginBottom: '8px',
          maxWidth: 'fit-content',
        },
        chips: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexShrink: 1,
          maxWidth: 'calc(40% - 20px)', // defined because we have 3 chips and an arrow icon
          minWidth: 'calc(15%)',
          backgroundColor: theme.palette.common.doss.gray[1],
          color: theme.palette.common.doss.gray[9],
          borderRadius: '2px',
          marginBottom: '8px',
        },
      },
    },
  }
}

interface ActivityTimelineProps {
  activities: (PaginatedEvent | PaginatedComment)[]
}

export enum ActivityEventItemType {
  CRUD = 'CRUD',
  COMMENT = 'COMMENT',
  WORKFLOW = 'WORKFLOW',
  INTEGRATION = 'INTEGRATION',
}

export function ActivityTimeline({ activities }: ActivityTimelineProps) {
  const styles = useStyles(getDossActivityTimelineStyles)

  return (
    <Timeline sx={styles.timeline}>
      {Children.toArray(
        activities.map((activity) => (
          <Box sx={styles.timelineItem}>
            <ActivityItem activity={activity} />
          </Box>
        )),
      )}
    </Timeline>
  )
}
